import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import posthog from 'posthog-js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('cardlockr_token');
    const savedUser = localStorage.getItem('cardlockr_user');

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      api.get('/auth/me')
        .then(res => {
          setUser(res.data);
          localStorage.setItem('cardlockr_user', JSON.stringify(res.data));
          posthog.identify(String(res.data.id), {
            email: res.data.email,
            name: res.data.name,
            role: res.data.role,
            plan: res.data.account?.plan,
            accountId: res.data.accountId,
          });
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const trustToken = localStorage.getItem('cardlockr_trust') || undefined;
    const res = await api.post('/auth/login', { email, password, trustToken });
    if (res.data.requires2FA) {
      return { requires2FA: true, method: res.data.method, tempToken: res.data.tempToken };
    }
    completeLogin(res.data.token, res.data.user);
    return res.data.user;
  };

  const completeLogin = (token, userData) => {
    localStorage.setItem('cardlockr_token', token);
    localStorage.setItem('cardlockr_user', JSON.stringify(userData));
    setUser(userData);
    posthog.identify(String(userData.id), {
      email: userData.email,
      name: userData.name,
      role: userData.role,
      plan: userData.account?.plan,
      accountId: userData.accountId,
    });
  };

  const logout = () => {
    // Invalidate JWT server-side before clearing local state
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem('cardlockr_token');
    localStorage.removeItem('cardlockr_user');
    setUser(null);
    posthog.reset();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, completeLogin, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
